import './styles.scss';

import { Drawer as MuiDrawer } from '@mui/material';
import { IconX } from '@tabler/icons-react';

import { Button, Text } from '@fragments';
import useModalZIndex from '@hooks/useModalZIndex';

/**
 * Drawer — a side sheet built on MUI `Drawer`.
 *
 * MUI handles the portal, backdrop, focus trap, ESC/backdrop-click dismissal,
 * and the slide+fade entrance. We restyle the paper with our tokens: it sits
 * flush to the viewport edge with only the INNER corners rounded.
 */
export default function Drawer({
    open,
    onClose,
    title,
    eyebrow,
    icon,
    side = 'right',
    width = 440,
    footer,
    headerActions,
    className = '',
    children,
    ...props
}) {
    // Share the global modal stack so a Drawer opened over a modal sits above it
    // and a stacked Drawer climbs another step. `zIndex` is the surface layer;
    // the backdrop renders one step below.
    const { zIndex, isTop } = useModalZIndex(open);

    const hasHeader = !!(title || eyebrow || icon || headerActions || onClose);

    return (
        <MuiDrawer
            anchor={side}
            open={open}
            onClose={onClose}
            disableEnforceFocus={!isTop}
            // App.css pins `.MuiModal-root` z-index to var(--modal-z, 9998) with
            // !important (a raw `zIndex` would be ignored), so feed the acquired
            // stack value through that var to layer above any modal beneath us.
            sx={{ '--modal-z': zIndex }}
            PaperProps={{
                className: `gst-drawer gst-drawer--${side} ${className}`.trim(),
                style: { '--gst-drawer-width': `${width}px` },
            }}
            {...props}
        >
            {hasHeader && (
                <div className="gst-drawer__header">
                    <div className="gst-drawer__heading">
                        {eyebrow && (
                            <Text.subheading className="gst-drawer__eyebrow">
                                {eyebrow}
                            </Text.subheading>
                        )}
                        {title && (
                            <Text.h5
                                weight={500}
                                contrast={9}
                                left={icon}
                                className="gst-drawer__title"
                            >
                                {title}
                            </Text.h5>
                        )}
                    </div>

                    <div className="gst-drawer__header-actions">
                        {headerActions}
                        {onClose && (
                            <Button
                                variant="text"
                                color="inverse"
                                size="small"
                                icon={<IconX />}
                                onClick={onClose}
                                className="gst-drawer__close"
                            />
                        )}
                    </div>
                </div>
            )}

            <div className="gst-drawer__body">{children}</div>

            {footer && <div className="gst-drawer__footer">{footer}</div>}
        </MuiDrawer>
    );
}
